import * as $ from 'jquery';
import { OnReadyModule } from '../../lib/app-module';
import { validateEmail, validateRequired } from '../../lib/validation';
import { UserModuleExport } from '../user/user';
import { App } from '../..';

interface SubscribePayload {
    u:  string;
    id: string;
    f_id: string;
    FNAME: string;
    EMAIL: string;
    subscribe: string;
    tags?: string
}

interface SubscribeEvent {
    tags: string[];
    name: string;
    email: string;
}

declare const window: Window & {app: App};

export interface MailchimpSubscribeConfig {
    accountId: string;
    audienceId: string;
    formId: string;
    primary: string;
    fallback: string;
}

export class MailchimpSubscribe extends OnReadyModule<MailchimpSubscribeConfig> {
    private subscribeForm: JQuery<HTMLElement>;
    private nameValidation: JQuery<HTMLElement>;
    private emailValidation: JQuery<HTMLElement>;
    private successResponse: JQuery<HTMLElement>;
    private errorResponse: JQuery<HTMLElement>;
    private nameField: JQuery<HTMLInputElement>;
    private emailField: JQuery<HTMLInputElement>;
    private tagsField: JQuery<HTMLElement>;
    private closeSuccess: JQuery<HTMLButtonElement>;
    private closeError: JQuery<HTMLButtonElement>;

    protected init(): void {
        this.subscribeForm = $('#mc-embedded-subscribe-form');
        this.nameValidation = this.subscribeForm.find('#field-name');
        this.emailValidation = this.subscribeForm.find('#field-email');
        this.successResponse = this.subscribeForm.find('#mce-success-response');
        this.errorResponse = this.subscribeForm.find('#mce-error-response');
        this.nameField = this.subscribeForm.find<HTMLInputElement>('input[name="FNAME"]');
        this.emailField = this.subscribeForm.find<HTMLInputElement>('input[name="EMAIL"]');
        this.tagsField = this.subscribeForm.find('input[name="tags"]');
        this.closeSuccess = $('#close-form-success');
        this.closeError = $('#close-form-error');
        this.monitorEvents();
        this.attemptPrefill();
    }

    private monitorEvents(): void {
        this.nameField.on('focusout',  () => this.validatedName());
        this.emailField.on('focusout',  () => this.validatedEmail());
        this.subscribeForm.on('submit', (event) => this.subscribe(event));
        this.closeError.on('click', () => this.errorResponse.hide());
        this.closeSuccess.on('click', () => this.successResponse.hide());
    }

    private attemptPrefill(): void {
        if (window.app.m.user) {
            const userMod = window.app.m.user as UserModuleExport;
            userMod.fillEmail(this.emailField[0]);
            userMod.fillName(this.nameField[0]);
        }
    }

    private async subscribe(event: JQuery.SubmitEvent): Promise<void> {
        event.preventDefault();
        this.successResponse.hide();
        this.errorResponse.hide();
        const name = this.validatedName();
        const email = this.validatedEmail();
        const tags = this.getTags();
        if (name && email) {
            await this.subscribeWithFallback({name, email, tags});
            await this.sendSubscribeEvent({name, email, tags});
        }
    }

    private validatedName(): string | false {
        const value = this.nameField.val();
        if (validateRequired(value)) {
            this.nameValidation.removeClass('error');
            return value;
        } else {
            this.nameValidation.addClass('error');
            return false;
        }
    }

    private validatedEmail(): string | false {
        const value = this.emailField.val();
        if (validateEmail(value)) {
            this.emailValidation.removeClass('error');
            return value;
        } else {
            this.emailValidation.addClass('error');
            return false;
        }
    }

    private async sendSubscribeEvent(event: SubscribeEvent): Promise<void> {
        return new Promise((resolve, reject) => {
            $.ajax({
                type: 'POST',
                url: 'https://api.desajustecreativo.com/subscribe',
                data: JSON.stringify(event),
                contentType: 'application/json',
                success: (() => {
                    resolve();
                }),
                error: (error => {
                    console.log(error);
                    reject(error);
                })
            });
        });
    }

    private async subscribeWithFallback({name, email, tags}: SubscribeEvent): Promise<void> {
        const payload = this.buildSubscribePayload({name, email, tags});
        try {
            await this.subscribeToHost(this.config.primary, payload);
        } catch (error) {
            console.log(error);
            try {
                await this.subscribeToHost(this.config.fallback, payload);
            } catch (error) {
                this.subscribeError(error);
                throw error;
            }
        }
    }

    private async subscribeToHost(host: string, payload: SubscribePayload): Promise<void> {
        const jsonpCallback = 'jsonpCallback';
        
        const response = await $.ajax({
            url: 'https://' + host + '/subscribe/post-json',
            data: {...payload, c: jsonpCallback},
            dataType: 'jsonp',
            jsonpCallback,
            xhrFields: {withCredentials: false}
        });
        if (response.result !== 'success') {
            throw new Error(response);
        }
        this.subscribeSuccess();
    }

    private buildSubscribePayload({name, email, tags}: {name: string, email: string, tags: string[]}): SubscribePayload {
        const challenge = 'b_' + this.config.accountId + '_' + this.config.audienceId;
        const payload = {
            u: this.config.accountId,
            id: this.config.audienceId,
            f_id: this.config.formId,
            FNAME: name,
            EMAIL: email,
            subscribe: '',
            [challenge]: ''
        };
        if (tags.length > 0) {
            payload.tags = tags.join(',');
        }
        return payload;
    }

    private getTags(): string[] {
        if (!this.tagsField) {
            return [];
        }
        return (this.tagsField.val() as string)
            .split(',')
            .map(tag => tag.trim());
    }
    
    private subscribeSuccess(): void {
        this.successResponse.show();
        this.subscribeForm.trigger('reset');
    }
    
    private subscribeError(error: unknown): void {
        console.log(error);
        this.errorResponse.show();
    }
}